/**
 * <hold-gallery-nav>
 *
 * Previous / next arrows laid over the product gallery's main image, so the
 * next photograph is one click away from the picture itself rather than a trip
 * down to the thumbnail strip.
 *
 * Why not Dawn's own arrows. They exist in the markup already, but they belong
 * to SliderComponent, which claims any button named "previous"/"next" inside
 * the component and works by scrolling the list sideways. In the thumbnail
 * layout every media item except the active one is display:none above 750px,
 * so SliderComponent.initPages() sees a single visible slide and gives up.
 * Taking those buttons over would also cost their real job on phones, where
 * the list genuinely is a scroll-snap slider.
 *
 * So this steps the gallery the same way the thumbnails do: media-gallery.js
 * exposes setActiveMedia(), which swaps is-active, plays the new media,
 * announces it to the live region, and syncs aria-current on the thumbnails.
 * Everything stays in one place and there is no second notion of "current".
 *
 * The ends wrap. They used to go spent, on the reasoning that the thumbnail
 * strip below was already showing the position - but measured, the strip is
 * under the fold at 1280x800 and 1440x900, the two commonest laptop sizes, so
 * a spent arrow there was a dead button with nothing on screen to explain it.
 * Wrapping means all five photographs are reachable without ever scrolling
 * down to the thumbnails, which is the whole point of putting arrows on the
 * picture. The cost is that the set has no end: the thumbnail strip is the
 * only thing saying which one you are on, and on those two sizes it needs a
 * scroll.
 */
if (!customElements.get('hold-gallery-nav')) {
  customElements.define(
    'hold-gallery-nav',
    class HoldGalleryNav extends HTMLElement {
      connectedCallback() {
        // closest, not getElementById: this element lives inside the gallery,
        // so it is exact, and it cannot resolve to the wrong one during a
        // section re-render when two media-gallery elements briefly coexist.
        this.gallery = this.closest('media-gallery');
        if (!this.gallery || typeof this.gallery.setActiveMedia !== 'function') return;

        this.buttons = Array.from(this.querySelectorAll('[data-hold-gallery-step]'));
        this.onClick = this.onClick.bind(this);
        this.buttons.forEach((button) => button.addEventListener('click', this.onClick));
      }

      disconnectedCallback() {
        this.buttons?.forEach((button) => button.removeEventListener('click', this.onClick));
      }

      /** Every media item in the viewer, in the order they are shown. */
      get items() {
        return Array.from(this.gallery.querySelectorAll('.product__media-item[data-media-id]'));
      }

      get currentIndex() {
        return this.items.findIndex((item) => item.classList.contains('is-active'));
      }

      onClick(event) {
        // The active image sits inside a modal-opener that opens the lightbox
        // on click, so the arrow must not let the event reach it.
        event.preventDefault();
        event.stopPropagation();

        const step = Number(event.currentTarget.dataset.holdGalleryStep) || 0;
        const items = this.items;
        const current = this.currentIndex;
        if (!items.length || current < 0) return;

        // Modulo twice so a step back from the first lands on the last: the
        // first % can return a negative in JavaScript.
        const next = ((current + step) % items.length + items.length) % items.length;
        this.gallery.setActiveMedia(items[next].dataset.mediaId, false);
      }
    }
  );
}
